import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Control,
  Controller,
  FieldErrors,
  SubmitHandler,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { useMutation } from '@apollo/client';

import { showToast } from '../../utils/toast.util';
import { CREATE_ACCOUNT_MUTATION } from '../../../core/graphql/mutation';
import { useNavState } from '../../components/hookts/useNavState';
import { client } from '../../../core/stores';
import { LoadingContent } from '../../components';
import { RegisterFormProps, RegisterFormSchema } from './Register.schema';
import { QUERY_REGISTRATION_EMAIL_AVAILABLE } from './RegisterForm.graphql';
import { limparRascunho, lerRascunho, salvarRascunho } from './RegisterDraft.storage';
import { TOTAL_STEPS, camposDoStep, normalizarStep } from './RegisterForm.steps';

interface IRegisterFormContext {
  Controller: typeof Controller;
  register: UseFormRegister<RegisterFormProps>;
  handleSubmit: UseFormHandleSubmit<RegisterFormProps>;
  control: Control<RegisterFormProps>;
  errors: FieldErrors<RegisterFormProps>;
  isSubmitting: boolean;
  step: number;
  onHandleSubmit: (evento?: React.FormEvent) => void;
  accountCategories?: any[];
  getValues: UseFormGetValues<RegisterFormProps>;
  goBack: () => void;
  setValue: UseFormSetValue<RegisterFormProps>;
  watch: UseFormWatch<RegisterFormProps>;
  setStep: (step: number) => void;
  emailIndisponivel: boolean;
  verificandoEmail: boolean;
  token?: string;
}

export const RegisterFormContext = React.createContext<IRegisterFormContext>({} as IRegisterFormContext);

type RegisterFormProviderProps = {
  accountCategories?: any[];
  token?: string;
  children: React.ReactNode;
}

export function RegisterFormProvider({children, accountCategories, token}: RegisterFormProviderProps) : JSX.Element {
  const [stepBruto, setStepBruto] = useNavState<string | number>('step', '1');
  const step = normalizarStep(stepBruto);
  const setStep = React.useCallback((valor: number) => setStepBruto(`${normalizarStep(valor)}`), [setStepBruto]);

  // `client()` constroi um ApolloClient novo a cada chamada; sem memoizar, cada
  // render criava outro cliente (e outro cache).
  const apollo = React.useMemo(() => client('global'), []);
  const [createAccount, {data, loading, error}] = useMutation(CREATE_ACCOUNT_MUTATION, {client: apollo});
  const dataLayer = global?.window?.dataLayer || [];
  const [isLoading, setIsLoading] = React.useState(false);
  const [emailIndisponivel, setEmailIndisponivel] = React.useState(false);
  const [verificandoEmail, setVerificandoEmail] = React.useState(false);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    trigger,
    setError,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<RegisterFormProps>({
    resolver: zodResolver(RegisterFormSchema),
    mode: 'onChange',
  });

  // Restaura o que o cadastro tinha antes de um refresh. Senha e CPF nao sao
  // guardados, entao quem recarregou depois do passo 1 volta para preencher a
  // senha de novo — o passo restaurado nunca passa do 1 por isso.
  React.useEffect(() => {
    const rascunho = lerRascunho();
    if (!rascunho) return;

    Object.entries(rascunho.campos).forEach(([campo, valor]) => {
      setValue(campo as keyof RegisterFormProps, valor as never, {shouldValidate: false});
    });

    if (!getValues('password')) setStep(1);
  }, []);

  const valoresAtuais = watch();

  React.useEffect(() => {
    salvarRascunho(valoresAtuais, step);
  }, [valoresAtuais, step]);

  const emailDisponivel = React.useCallback(async (email: string): Promise<boolean> => {
    setVerificandoEmail(true);

    try {
      const {data: resposta} = await apollo.query({
        query: QUERY_REGISTRATION_EMAIL_AVAILABLE,
        variables: {email},
        fetchPolicy: 'network-only',
      });

      return resposta?.registrationEmailAvailable !== false;
    } catch {
      // Falha de rede nao pode travar o cadastro: a mutation ainda e a
      // autoridade sobre e-mail duplicado.
      return true;
    } finally {
      setVerificandoEmail(false);
    }
  }, [apollo]);

  const goBack = React.useCallback(() => setStep(step - 1), [setStep, step]);

  const avancar = React.useCallback(async () => {
    const valido = await trigger(camposDoStep(step));
    if (!valido) return;

    if (step === 1) {
      const disponivel = await emailDisponivel(getValues('email'));

      if (!disponivel) {
        setEmailIndisponivel(true);
        setError('email', {message: 'Já existe uma conta com esse e-mail.'});
        return;
      }

      setEmailIndisponivel(false);
    }

    // O dataLayer recebia o formulario inteiro — senha, CPF, telefone, e-mail e
    // endereco iam para o Google Tag Manager a cada passo.
    dataLayer.push({event: 'register', step: `${step}`});
    setStep(step + 1);
  }, [dataLayer, emailDisponivel, getValues, setError, setStep, step, trigger]);

  const enviar: SubmitHandler<RegisterFormProps> = React.useCallback((dados) => {
    const partes = dados.name.trim().split(' ').filter(Boolean);
    const {avatar_preview: _preview, ...resto} = dados;

    setIsLoading(true);
    createAccount({
      variables: {
        ...resto,
        name: partes[0],
        lastName: partes.slice(1).join(' '),
        avatar: dados.avatar ?? null,
      },
    });
  }, [createAccount]);

  const onHandleSubmit = React.useCallback(
    (evento?: React.FormEvent) => {
      evento?.preventDefault();

      if (step < TOTAL_STEPS) return avancar();

      return handleSubmit(enviar)();
    },
    [avancar, enviar, handleSubmit, step],
  );

  React.useEffect(() => {
    if (!data?.createAccount) return;

    dataLayer.push({event: 'register', step: 'success'});
    limparRascunho();
    setIsLoading(true);
    showToast({html: 'Conta criada com sucesso! Já pode acessar o sistema.'});
    window.location.href = `/${data.createAccount.account.code}/admin`;
  }, [data]);

  React.useEffect(() => {
    if (!error) return;

    setIsLoading(false);

    // O servidor marca e-mail duplicado com um codigo proprio: quando ele vem,
    // o certo e voltar ao passo onde o e-mail foi digitado.
    const duplicado = error.graphQLErrors?.some((e: any) => e.extensions?.code === 'EMAIL_TAKEN');

    if (duplicado) {
      setEmailIndisponivel(true);
      setError('email', {message: 'Já existe uma conta com esse e-mail.'});
      setStep(1);
      showToast({html: 'Já existe uma conta com esse e-mail. Entre ou recupere sua senha.'});
      return;
    }

    showToast({html: error.message || 'Erro ao criar conta. Por favor, tente novamente.'});
  }, [error]);

  if (loading) return <LoadingContent loading={loading || isLoading} />;

  return (
    <RegisterFormContext.Provider
      value={{
        Controller,
        register,
        handleSubmit,
        control,
        errors,
        isSubmitting,
        step,
        setValue,
        onHandleSubmit,
        accountCategories,
        getValues,
        goBack,
        watch,
        token,
        setStep,
        emailIndisponivel,
        verificandoEmail,
      }}>
      {children}
    </RegisterFormContext.Provider>
  );
}
